import Button from '../shared/Button.js';
// CattleForecastView — flagship visual planning surface for the cattle program.
// Replaces Nick's manual spreadsheet finisher forecast workflow.
//
// Locked behaviors live in src/lib/cattleForecast.js (pure deterministic math).
// This file is UI wiring + read/write through src/lib/cattleForecastApi.js.
//
// Edit-permission tiering (management/admin vs farm_team) is enforced HERE,
// in the UI, per the build packet decision. RLS on the three forecast tables
// is broad-authenticated; the database itself doesn't filter writes by role.
import React from 'react';
import {AppLink, useAppNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import CowDetail from './CowDetail.tsx';
import {
  buildForecast,
  monthLabel,
  monthStartMs,
  parseMonthKey,
  formatAdgCalc,
  isHeiferEligibleForInclude,
  projectedWeightAtMonth,
  WATCHLIST_REASONS,
} from '../lib/cattleForecast.js';
import {saveForecastSettings, saveHeiferIncludes, setHidden} from '../lib/cattleForecastApi.js';
import {newCattleRequestId, runCattleOperation} from '../lib/cattleOperationsApi.js';
import {CATTLE_HERD_KEYS, cowTagSet} from '../lib/cattleHerdFilters.js';

import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import Badge from '../shared/Badge.jsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import {reportSaved, reportSaveFailed, reportSaving} from '../lib/saveStatus.js';
import {formatRuminantAge, modalBackdropStyle, modalDialogStyle} from './ruminantPresentation.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import RecordCollaborationSection from '../shared/RecordCollaborationSection.jsx';
import {recordFieldChange} from '../lib/entityMutations.js';
import {buildChanges, countSummary} from '../lib/activityChangeDiff.js';
import {sb} from '../lib/supabase.js';
import {fmt} from '../lib/dateUtils.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthState} from '../contexts/AuthContext.jsx';
import Header from '../shared/Header.tsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleForecastCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {MUTATION} from '../app/data/routeMutationCatalog.js';
import type {CattleAnimalPatchVars} from '../app/data/routeMutationCatalog.js';
import {useRouteMutation} from '../app/data/useRouteMutation.js';
import type {CattleRecord} from '../lib/cattleHerdFilters.js';
import type {
  CattleProcessingBatchRow,
  ForecastAnimalRow,
  ForecastHiddenRow,
  ForecastMonthBucket,
  ForecastResult,
  ForecastSettings,
  ForecastWeighInRow,
} from '../lib/cattleForecast.js';
import './cattleRedesign.css';
import './cattleForecastBatchesRedesign.css';

type AnyRecord = Record<string, any>;
type ForecastCattle = CattleRecord & AnyRecord;
type Notice = {kind?: 'error' | 'warning' | 'success' | 'info'; message?: string | null};
type PersistentSetter<T> = (nextValue: T | ((previous: T) => T)) => void;
type TagMatcher = (input: {cow?: ForecastCattle; cowRow?: ForecastCattle; tag?: string | number | null}) => boolean;
type HeiferBadge = {label: string; variant: 'neutral' | 'info' | 'ok'};
type ColdBootRecovery = {
  scope: string | null;
  attempts: number;
  settled: boolean;
  pendingScope: string | null;
};

// The same three lines CattleHerdsView, CattleAnimalPage, CattleBatchPage and
// HomeDashboard each carry, and for the same reason: the catalog rethrows
// PostgREST's `error.message` unchanged, so reading `.message` off the
// rejection reproduces the notice text `runMutation`'s `onError(msg)` produced
// — including the "undefined" a message-less error would have rendered before.
// This is a conversion, not a rewording.
type ErrorLike = {message?: string};
const writeErrorMessage = (err: unknown): string =>
  err && typeof err === 'object' ? String((err as ErrorLike).message) : String(err);

const CATTLE_EXCLUDE = ['herd', 'processing_batch_id'];
const CATTLE_LABELS = {
  tag: 'Tag',
  sex: 'Sex',
  breed: 'Breed',
  origin: 'Origin',
  birth_date: 'Birth date',
  purchase_date: 'Purchase date',
  purchase_amount: 'Purchase amount',
  dam_tag: 'Dam tag',
  sire_tag: 'Sire tag',
  registration_num: 'Registration #',
  pct_wagyu: '% Wagyu',
  breeding_status: 'Breeding status',
  breeding_blacklist: 'Breeding blacklist',
  sale_date: 'Sale date',
  sale_amount: 'Sale amount',
  death_date: 'Death date',
  death_reason: 'Death reason',
  old_tags: 'Prior tags',
};
const CATTLE_FORMATTERS: Record<string, (value: any) => string> = {
  old_tags: (v: any) => countSummary(v, 'prior tag'),
};

const HERD_LABELS: Record<string, string> = {
  mommas: 'Mommas',
  backgrounders: 'Backgrounders',
  finishers: 'Finishers',
  bulls: 'Bulls',
  processed: 'Processed',
  deceased: 'Deceased',
  sold: 'Sold',
};

const WATCHLIST_REASON_LABELS: Record<string, string> = {
  [WATCHLIST_REASONS.NO_WEIGHT_NO_DOB]: 'No weight + no DOB',
  [WATCHLIST_REASONS.NEGATIVE_ADG_NO_FINISH]: 'Negative ADG, no finish month',
  [WATCHLIST_REASONS.NEVER_REACHES_WINDOW]: 'Never reaches display window',
  [WATCHLIST_REASONS.ALREADY_OVER_MAX]: 'Already over max weight',
  [WATCHLIST_REASONS.PROJECTS_PAST_MAX]: 'Projects past max by current month',
  [WATCHLIST_REASONS.ALL_ELIGIBLE_HIDDEN]: 'All eligible months hidden',
};

const inpS: React.CSSProperties = {
  fontSize: 13,
  padding: '7px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 10,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const lbl = {fontSize: 11, color: 'var(--ink-muted)', display: 'block', marginBottom: 3, fontWeight: 500};

const tile = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  padding: '14px 16px',
};

const sectionHeader = {fontSize: 13, fontWeight: 600, color: 'var(--ink-muted)', marginBottom: 8};

const CATTLE_FORECAST_PARAMS = Object.freeze({});
const EMPTY_FORECAST_ROWS = Object.freeze([]) as readonly never[];
const EMPTY_FORECAST_INCLUDES = new Set<string>();
const COLD_BOOT_RETRY_BACKOFFS_MS = Object.freeze([350, 800]);

// Compact age string from birth_date (YYYY-MM-DD) to a target Date.
// Renders as "Xy Ym" (e.g. "2y 4m"), or just "Ym" when under a year, or "—".
// Forecast months always anchor to YYYY-MM-15 via monthStartMs (lib helper).
function ageAt(birthDate: string | null | undefined, targetMs: number) {
  if (!birthDate) return '—';
  const birthMs = new Date(String(birthDate) + 'T12:00:00Z').getTime();
  if (!Number.isFinite(birthMs) || !Number.isFinite(targetMs)) return '—';
  const days = Math.floor((targetMs - birthMs) / 86400000);
  if (days < 0) return '—';
  const y = Math.floor(days / 365);
  const m = Math.floor((days % 365) / 30);
  return y > 0 ? y + 'y ' + m + 'm' : m + 'm';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const CattleForecastView = () => {
  const {authState, rolePreview, sessionEpoch} = useAuth();
  const {useState, useEffect, useMemo, useRef} = React;

  // Role gate — UI-only.
  const role = authState ? authState.role : null;
  const authIdentity = authState || null;
  const canEdit = role === 'admin' || role === 'management';

  // Loaded state.
  const {
    data: forecastResourceData,
    loading: forecastResourceLoading,
    error: forecastResourceError,
    refetch: refetchForecastResource,
  } = useRouteResource(RESOURCE.CATTLE_FORECAST_CORE, CATTLE_FORECAST_PARAMS);
  const resourceData = forecastResourceData as CattleForecastCoreData | null;
  const forecastScope =
    authState && typeof authState === 'object'
      ? `${sessionEpoch}:${authState.user.id}:${authState.role}:${rolePreview || ''}`
      : 'unresolved';
  // A passive-effect cleanup runs after a new route scope has committed. Keep
  // this render-time stamp so an old recovery timer cannot invoke the stable
  // refetch callback after it has retargeted the new identity.
  const forecastScopeRef = useRef(forecastScope);
  forecastScopeRef.current = forecastScope;
  const [localForecast, setLocalForecast] = useState<{scope: string; data: CattleForecastCoreData} | null>(null);
  // settingsDraft is what the editable controls bind to. settings (below)
  // is what buildForecast actually consumes — only swapped on Save Settings.
  const [settingsDraft, setSettingsDraft] = useState<ForecastSettings | null>(null);
  const [recoveringColdBoot, setRecoveringColdBoot] = useState(false);
  const coldBootRecovery = useRef<ColdBootRecovery>({
    scope: null,
    attempts: 0,
    settled: false,
    pendingScope: null,
  });
  useEffect(() => {
    if (!resourceData) return;
    setLocalForecast({scope: forecastScope, data: resourceData});
    setSettingsDraft(resourceData.settings);
  }, [forecastScope, resourceData]);

  // CP2 cold-boot recovery remains a surface concern, but the surface no
  // longer owns a loader: every bounded retry calls the scope-keyed route
  // resource's refetch. An empty cattle spine, cattle-without-weigh-ins, or a
  // first-load error are all ambiguous at boot. Retry twice before treating an
  // empty farm or error as settled; the uncached resource primitive means no
  // module-global weigh-in cache needs invalidating between attempts.
  useEffect(() => {
    const recovery = coldBootRecovery.current;
    if (recovery.scope !== forecastScope) {
      recovery.scope = forecastScope;
      recovery.attempts = 0;
      recovery.settled = false;
      recovery.pendingScope = null;
    }
    if (forecastResourceLoading) return;

    const coreEmpty = resourceData?.cattle.length === 0;
    const weighInsSuspectEmpty = !!resourceData && resourceData.cattle.length > 0 && resourceData.weighIns.length === 0;
    // A same-key refresh can retain good data alongside an error. That is not
    // a cold boot and must stay a user-directed Retry, not an automatic loop.
    const failedBeforeData = !resourceData && !!forecastResourceError;
    const coldBootSuspect = failedBeforeData || coreEmpty || weighInsSuspectEmpty;

    if (!coldBootSuspect) {
      recovery.settled = true;
      recovery.attempts = 0;
      setRecoveringColdBoot(false);
      return;
    }
    if (recovery.settled) {
      setRecoveringColdBoot(false);
      return;
    }
    if (recovery.attempts >= COLD_BOOT_RETRY_BACKOFFS_MS.length) {
      recovery.settled = true;
      setRecoveringColdBoot(false);
      return;
    }
    // The effect may run again while a timer is pending (including React's
    // development effect replay). One scope has exactly one pending resource
    // refetch, so a re-render cannot duplicate the raw request.
    if (recovery.pendingScope === forecastScope) return;

    const attempt = recovery.attempts;
    const scheduledScope = forecastScope;
    recovery.pendingScope = scheduledScope;
    setRecoveringColdBoot(true);
    const timer = setTimeout(() => {
      if (
        forecastScopeRef.current !== scheduledScope ||
        recovery.scope !== scheduledScope ||
        recovery.pendingScope !== scheduledScope
      ) {
        return;
      }
      recovery.pendingScope = null;
      recovery.attempts = attempt + 1;
      void refetchForecastResource();
    }, COLD_BOOT_RETRY_BACKOFFS_MS[attempt]);

    return () => {
      clearTimeout(timer);
      if (recovery.pendingScope === scheduledScope) recovery.pendingScope = null;
    };
  }, [forecastResourceError, forecastResourceLoading, forecastScope, refetchForecastResource, resourceData]);

  const forecastData = localForecast?.scope === forecastScope ? localForecast.data : resourceData;
  const cattle = (forecastData?.cattle || EMPTY_FORECAST_ROWS) as ForecastCattle[];
  const weighIns = (forecastData?.weighIns || EMPTY_FORECAST_ROWS) as ForecastWeighInRow[];
  const calvingRecs = (forecastData?.calvingRecords || EMPTY_FORECAST_ROWS) as AnyRecord[];
  const comments = (forecastData?.comments || EMPTY_FORECAST_ROWS) as AnyRecord[];
  const breedOpts = (forecastData?.breedOptions || EMPTY_FORECAST_ROWS) as AnyRecord[];
  const originOpts = (forecastData?.originOptions || EMPTY_FORECAST_ROWS) as AnyRecord[];
  const realBatches = (forecastData?.processingBatches || EMPTY_FORECAST_ROWS) as CattleProcessingBatchRow[];
  const settings = forecastData?.settings || null;
  const includes = forecastData?.includes || EMPTY_FORECAST_INCLUDES;
  const hidden = (forecastData?.hidden || EMPTY_FORECAST_ROWS) as ForecastHiddenRow[];
  const loading = forecastResourceLoading || recoveringColdBoot;
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Inline notice for forecast settings save / hide-state update / heifer
  // selection save errors. Cleared at the start of each action handler.
  const [notice, setNotice] = useState<Notice | null>(null);
  const [yearFilter, setYearFilter] = usePersistentViewState<number | null>('cattle.forecast.yearFilter', null); // null = current year by default
  const [monthFilter, setMonthFilter] = usePersistentViewState<string | null>('cattle.forecast.monthFilter', null);
  const [showHeiferModal, setShowHeiferModal] = useState(false);
  // Tag search — client-side filter applied across planned rows, hidden
  // rows, actual-batch rows, and Attention rows. Empty string = show all.
  const [tagSearch, setTagSearch] = usePersistentViewState<string>('cattle.forecast.tagSearch', '');
  const tagSearchTrim = tagSearch.trim().toLowerCase();
  // Predicate every renderable row uses. Matches against current cow.tag
  // PLUS retag-aware prior tags via cowTagSet so old tag numbers still find
  // the cow. For actual-batch rows the cow record may have been re-herded
  // already; cowRow may be missing — fall back to the cows_detail tag.
  function tagMatch({cow, cowRow, tag}: {cow?: ForecastCattle; cowRow?: ForecastCattle; tag?: string | number | null}) {
    if (!tagSearchTrim) return true;
    const candidates = [];
    if (cow) {
      for (const t of cowTagSet(cow)) candidates.push(String(t).toLowerCase());
    }
    if (cowRow && cowRow !== cow) {
      for (const t of cowTagSet(cowRow)) candidates.push(String(t).toLowerCase());
    }
    if (tag) candidates.push(String(tag).toLowerCase());
    return candidates.some((c) => c.includes(tagSearchTrim));
  }

  // Every forecast render input comes from the scope-keyed route resource.
  // A Retry is a Query refetch, not a second page-local loader, so an
  // unavailable input cannot be mistaken for an empty forecast.

  function updateForecastData(update: (current: CattleForecastCoreData) => CattleForecastCoreData) {
    setLocalForecast((current) => {
      const data = current?.scope === forecastScope ? current.data : resourceData;
      return data ? {scope: forecastScope, data: update(data)} : current;
    });
  }

  async function loadAll() {
    await refetchForecastResource();
  }

  // Initial year filter = current year
  useEffect(() => {
    if (yearFilter == null && !loading) {
      setYearFilter(new Date().getUTCFullYear());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Build forecast on every relevant input change. Heavy but tractable
  // (n cattle ~500, n months ~48 → ~24k iterations).
  const forecast = useMemo<ForecastResult | null>(() => {
    if (!settings) return null;
    // Split scheduled rows out of realBatches so buildForecast can
    // suppress matching virtual batches and reserve their names. mig 054
    // added 'scheduled' as a valid status; until that migration runs,
    // this filter is a no-op (no scheduled rows exist).
    const realActiveOrComplete = (realBatches || []).filter((b) => b.status === 'active' || b.status === 'complete');
    const scheduledOnly = (realBatches || []).filter((b) => b.status === 'scheduled');
    return buildForecast({
      cattle,
      weighIns,
      settings,
      includes,
      hidden,
      realBatches: realActiveOrComplete,
      scheduledBatches: scheduledOnly,
      todayMs: Date.now(),
    });
  }, [cattle, weighIns, settings, includes, hidden, realBatches]);
  const summaryYear = new Date().getUTCFullYear();

  // ── settings panel handlers ────────────────────────────────────────────────
  function patchDraft(patch: Partial<ForecastSettings>) {
    setSettingsDraft((prev) => ({...(prev || {}), ...patch}));
  }
  async function saveSettingsClick() {
    if (!canEdit || !settingsDraft || !settings) return;
    setNotice(null);
    setSavingSettings(true);
    try {
      // Migration 240: the audited forecast_settings.save operation. The
      // expected-before is the LOADED settings this surface read; updated_by
      // is server-stamped from the authenticated profile.
      await saveForecastSettings(sb, settingsDraft, settings);
      updateForecastData((current) => ({...current, settings: settingsDraft}));
    } catch (e) {
      setNotice({kind: 'error', message: 'Could not save Forecast settings: ' + errorMessage(e)});
    } finally {
      setSavingSettings(false);
    }
  }
  function discardSettingsDraft() {
    setSettingsDraft(settings);
  }

  // ── hide/unhide ────────────────────────────────────────────────────────────
  // Migration 240: the toggle is ONE audited hidden.set operation — the write
  // and its Activity row (the client-mirrored status.changed on the
  // cattle.forecast stream, scoped by payload.month_key exactly as before)
  // land in the operation's transaction, so the old best-effort client
  // Activity emitter is gone. `currentlyHidden` is this surface's read state;
  // the operation applies only from it.
  async function toggleHidden(cattleId: string, monthKey: string, currentlyHidden: boolean) {
    if (!canEdit) return;
    setNotice(null);
    try {
      await setHidden(sb, {cattleId, monthKey, currentlyHidden});
      updateForecastData((current) => ({
        ...current,
        hidden: currentlyHidden
          ? current.hidden.filter((h) => !(h.cattle_id === cattleId && h.month_key === monthKey))
          : [...current.hidden, {cattle_id: cattleId, month_key: monthKey}],
      }));
    } catch (e) {
      setNotice({kind: 'error', message: 'Could not update hide state: ' + errorMessage(e)});
    }
  }

  // ── heifer-modal save ─────────────────────────────────────────────────────
  async function saveHeiferIncludesAndClose(nextSet: ReadonlySet<string>) {
    if (!canEdit) {
      setShowHeiferModal(false);
      return;
    }
    setNotice(null);
    try {
      // Migration 240: ONE heifer.apply operation per Confirm Selections;
      // included_at/included_by are server-stamped.
      await saveHeiferIncludes(sb, nextSet);
      updateForecastData((current) => ({...current, includes: new Set(nextSet)}));
    } catch (e) {
      setNotice({kind: 'error', message: 'Could not save heifer selections: ' + errorMessage(e)});
      return;
    }
    setShowHeiferModal(false);
  }

  // ── year / month filter UX ────────────────────────────────────────────────
  const yearsInView = useMemo(() => {
    if (!forecast) return [];
    const ys = new Set<number>();
    for (const b of forecast.monthBuckets) ys.add(b.year);
    // Past actual years from real batches (so we can show historical context).
    for (const rb of realBatches || []) {
      const dt = rb.actual_process_date || rb.planned_process_date;
      if (!dt) continue;
      const y = parseInt(String(dt).slice(0, 4), 10);
      if (Number.isFinite(y)) ys.add(y);
    }
    return [...ys].sort((a, b) => a - b);
  }, [forecast, realBatches]);

  const filteredMonthBuckets = useMemo(() => {
    if (!forecast) return [];
    return forecast.monthBuckets.filter((b) => (yearFilter == null ? true : b.year === yearFilter));
  }, [forecast, yearFilter]);

  const orderedMonthBuckets = useMemo(() => {
    const now = new Date();
    const currentMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    return [...filteredMonthBuckets].sort((a, b) => {
      const aPast = a.monthKey < currentMonth;
      const bPast = b.monthKey < currentMonth;
      return Number(aPast) - Number(bPast) || a.monthKey.localeCompare(b.monthKey);
    });
  }, [filteredMonthBuckets]);

  const yearSummaries = useMemo(() => {
    const totals = new Map<number, {processed: number; projected: number}>();
    for (const bucket of forecast?.monthBuckets || []) {
      const total = totals.get(bucket.year) || {processed: 0, projected: 0};
      total.projected += bucket.count;
      totals.set(bucket.year, total);
    }
    for (const batch of realBatches || []) {
      if (batch.status !== 'active' && batch.status !== 'complete') continue;
      const processDate = batch.actual_process_date || batch.planned_process_date;
      if (!processDate) continue;
      const year = parseInt(String(processDate).slice(0, 4), 10);
      if (!Number.isFinite(year)) continue;
      const total = totals.get(year) || {processed: 0, projected: 0};
      total.processed += Array.isArray(batch.cows_detail) ? batch.cows_detail.length : 0;
      totals.set(year, total);
    }
    return totals;
  }, [forecast, realBatches]);

  // Past actual batches grouped by year for the historical year view.
  const actualBatchesByYear = useMemo(() => {
    const map = new Map<number, CattleProcessingBatchRow[]>();
    for (const rb of realBatches || []) {
      const dt = rb.actual_process_date || rb.planned_process_date;
      if (!dt) continue;
      const y = parseInt(String(dt).slice(0, 4), 10);
      if (!Number.isFinite(y)) continue;
      const rows = map.get(y);
      if (rows) rows.push(rb);
      else map.set(y, [rb]);
    }
    return map;
  }, [realBatches]);

  // Tag → cow (for showing tags in summary panels).
  const cowsById = useMemo(() => new Map(cattle.filter((c) => c.id).map((c) => [c.id as string, c])), [cattle]);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="cattle-redesign cattle-forecast">
      <Header />
      <div className="cattle-page">
        <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />
        {/* Title + actions */}
        <div className="cattle-forecast-title-row" data-cattle-forecast-root>
          <div>
            <h1 className="cattle-page-title">Forecast</h1>
            <p className="cattle-page-summary">
              Projected finish weights by month · based on weigh-in history and forecast settings
            </p>
          </div>
          {!canEdit && (
            <span data-forecast-readonly>
              <Badge variant="info">READ-ONLY</Badge>
            </span>
          )}
          <span style={{flex: 1}} />
          {canEdit && (
            <Button
              variant="secondary"
              type="button"
              className="cattle-button"
              onClick={() => setShowHeiferModal(true)}
              data-include-heifers-btn
            >
              Include Momma Herd Heifers
              {includes.size > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: '1px 7px',
                    borderRadius: 10,
                    background: 'var(--surface-2)',
                    color: 'var(--text-primary)',
                    fontSize: 11,
                  }}
                >
                  {includes.size}
                </span>
              )}
            </Button>
          )}
          <Button
            variant="secondary"
            type="button"
            className="cattle-button"
            onClick={() => setShowSettings((v) => !v)}
            aria-expanded={showSettings}
            aria-controls="cattle-forecast-settings"
          >
            {showSettings ? 'Hide settings' : 'Settings'}
          </Button>
        </div>

        {/* ── Tag search (client-side filter across all rows) ────────── */}
        {!loading && Boolean(forecastResourceError) && (
          <div
            data-cattle-forecast-load-error="1"
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '14px 16px',
              color: 'var(--danger-ink)',
              fontSize: 13,
            }}
          >
            Could not load forecast data. Please try again. ({errorMessage(forecastResourceError)})
            <div>
              <Button
                variant="secondary"
                type="button"
                onClick={() => refetchForecastResource()}
                style={{marginTop: 10}}
              >
                Retry
              </Button>
            </div>
          </div>
        )}

        {!loading && forecast && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
            }}
          >
            <input
              type="text"
              value={tagSearch}
              onChange={(e) => setTagSearch(e.target.value)}
              placeholder="Search by tag #…"
              aria-label="Search forecast cattle by tag"
              data-forecast-tag-search
              style={{
                fontSize: 13,
                padding: '7px 10px',
                border: '1px solid var(--border-strong)',
                borderRadius: 10,
                fontFamily: 'inherit',
                width: '100%',
                maxWidth: 320,
                boxSizing: 'border-box',
              }}
            />
            {tagSearch && (
              <button
                type="button"
                className="cattle-button"
                onClick={() => setTagSearch('')}
                data-forecast-tag-search-clear
              >
                Clear
              </button>
            )}
          </div>
        )}

        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading{'…'}</div>}

        {!loading && forecast && (
          <>
            {/* ── All-years summary strip ─────────────────────────────── */}
            <div data-forecast-summary-strip>
              <div style={sectionHeader}>Summary</div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10}}>
                <SummaryTile
                  label="Eligible cattle"
                  value={forecast.summary.totalEligible.toLocaleString()}
                  sub={forecast.summary.finishCandidates + ' finish candidates on farm'}
                />
                {[summaryYear, summaryYear + 1, summaryYear + 2, summaryYear + 3].map((year) => {
                  const {processed, projected} = yearSummaries.get(year) || {processed: 0, projected: 0};
                  return (
                    <SummaryTile
                      key={year}
                      label={String(year)}
                      value={(processed + projected).toLocaleString()}
                      sub={`${processed.toLocaleString()} processed · ${projected.toLocaleString()} projected`}
                      labelStrong
                    />
                  );
                })}
                <SummaryTile
                  label="Watchlist"
                  value={forecast.summary.watchlistCount.toLocaleString()}
                  color={forecast.summary.watchlistCount > 0 ? 'var(--warn-ink)' : 'var(--text-primary)'}
                />
              </div>
            </div>

            {/* ── Next Processor Batch panel ──────────────────────────── */}
            <div style={tile} data-next-processor-panel>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--ink-muted)',
                    fontWeight: 600,
                  }}
                >
                  Next Processor Batch
                </span>
                {forecast.nextProcessorBatch ? (
                  <>
                    {/* Booked or forecast (Q14-A): a booking reads in black with a gray
                        "Booked"; a forecast month in gray with "est.". */}
                    <span
                      data-next-processor-month
                      data-next-processor-booked={forecast.nextProcessorBatch.source === 'scheduled' ? 'true' : 'false'}
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color:
                          forecast.nextProcessorBatch.source === 'scheduled'
                            ? 'var(--text-primary)'
                            : 'var(--ink-muted)',
                      }}
                    >
                      {forecast.nextProcessorBatch.label} · {forecast.nextProcessorBatch.animalIds.length}{' '}
                      {forecast.nextProcessorBatch.animalIds.length === 1 ? 'cow' : 'cows'}
                    </span>
                    <span style={{fontSize: 12, fontWeight: 600, color: 'var(--ink-muted)'}}>
                      {forecast.nextProcessorBatch.source === 'scheduled' ? 'Booked' : 'est.'}
                    </span>
                    <span style={{fontSize: 13, color: 'var(--ink-muted)'}}>{forecast.nextProcessorBatch.name}</span>
                    {forecast.nextProcessorBatch.projectedTotalLbs > 0 && (
                      <span style={{fontSize: 12, color: 'var(--text-primary)', fontWeight: 600}}>
                        {Math.round(forecast.nextProcessorBatch.projectedTotalLbs).toLocaleString()} lb projected
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{fontSize: 13, color: 'var(--ink-muted)', fontStyle: 'italic'}}>
                    No planned batch — no eligible cattle land in the display window.
                  </span>
                )}
              </div>
              {forecast.nextProcessorBatch && forecast.nextProcessorBatch.animalIds.length > 0 && (
                <div style={{marginTop: 10}}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'var(--ink-muted)',
                      marginBottom: 4,
                    }}
                  >
                    Allowed tags (Send-to-Processor must match this set)
                  </div>
                  <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}} data-next-processor-tags>
                    {[...forecast.nextProcessorBatch.allowedTagSet].sort().map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '3px 9px',
                          borderRadius: 999,
                          background: 'var(--surface-2)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Settings panel (collapsible; read-only for farm_team) ── */}
            {showSettings && settingsDraft && (
              <div style={tile} data-forecast-settings-panel id="cattle-forecast-settings">
                <div style={sectionHeader}>Settings</div>
                {!canEdit && (
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--info)',
                      background: 'var(--info-soft)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '6px 10px',
                      marginBottom: 10,
                    }}
                  >
                    Read-only view. Management or admin can edit forecast settings.
                  </div>
                )}
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10}}>
                  <NumField
                    label="Display weight min (lb)"
                    value={settingsDraft.displayMin}
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({displayMin: v})}
                  />
                  <NumField
                    label="Display weight max (lb)"
                    value={settingsDraft.displayMax}
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({displayMax: v})}
                  />
                  <NumField
                    label="Fallback ADG (lb/day)"
                    value={settingsDraft.fallbackAdg}
                    step="0.01"
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({fallbackAdg: v})}
                  />
                  <NumField
                    label="Birth weight (lb)"
                    value={settingsDraft.birthWeight}
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({birthWeight: v})}
                  />
                  <NumField
                    label="Horizon (years)"
                    value={settingsDraft.horizonYears}
                    min="1"
                    max="5"
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({horizonYears: v})}
                  />
                  <NumField
                    label="Monthly capacity (optional)"
                    value={settingsDraft.monthlyCapacity}
                    disabled={!canEdit}
                    onChange={(v) => patchDraft({monthlyCapacity: v})}
                  />
                </div>
                {canEdit && (
                  <div style={{display: 'flex', gap: 8, marginTop: 12, alignItems: 'center'}}>
                    <Button
                      variant="primary"
                      type="button"
                      className="cattle-button cattle-button-primary"
                      onClick={saveSettingsClick}
                      disabled={savingSettings}
                      data-save-settings-btn
                    >
                      {savingSettings ? 'Saving…' : 'Save Settings'}
                    </Button>
                    <Button variant="secondary" type="button" className="cattle-button" onClick={discardSettingsDraft}>
                      Discard
                    </Button>
                    <span style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                      Settings edits don't recompute the forecast until saved.
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── Attention section (always-rendered; empty state when none).
                Lifted above the year selector + month tiles so an operator
                can spot tag-#241-style anomalies without scrolling. */}
            <AttentionSection
              rows={forecast.watchlist}
              canEdit={canEdit}
              onToggleHidden={toggleHidden}
              fmt={fmt}
              tagMatch={tagMatch}
            />

            {/* ── Year selector ───────────────────────────────────────── */}
            {yearsInView.length > 0 && (
              <div style={{display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap'}}>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600, marginRight: 4}}>Year</span>
                {yearsInView.map((y) => (
                  <button
                    key={y}
                    type="button"
                    className={'cattle-button cattle-forecast-year-button' + (yearFilter === y ? ' is-selected' : '')}
                    aria-pressed={yearFilter === y}
                    onClick={() => {
                      setYearFilter(y);
                      setMonthFilter(null);
                    }}
                    data-year-button={y}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}

            {/* ── Visual main chart: projected head count by month ────── */}
            {/* F06: the chart keeps its title and a gray line when there are no months. */}
            <ForecastChart
              buckets={filteredMonthBuckets}
              actualBatches={yearFilter == null ? [] : actualBatchesByYear.get(yearFilter) || []}
            />

            {/* ── Month buckets (assigned visible cattle) ─────────────── */}
            <div data-forecast-month-buckets>
              <div style={sectionHeader}>Months</div>
              <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                {orderedMonthBuckets.map((b) => (
                  <MonthBucketTile
                    key={b.monthKey}
                    sb={sb}
                    authState={authState}
                    bucket={b}
                    forecast={forecast}
                    cowsById={cowsById}
                    canEdit={canEdit}
                    onToggleHidden={toggleHidden}
                    monthFilter={monthFilter}
                    setMonthFilter={setMonthFilter}
                    fmt={fmt}
                    tagMatch={tagMatch}
                  />
                ))}
              </div>
            </div>

            {/* ── Past actuals — historical actual batches for context ── */}
            {yearFilter != null && actualBatchesByYear.has(yearFilter) && (
              <PastActuals batches={actualBatchesByYear.get(yearFilter) || []} fmt={fmt} />
            )}
          </>
        )}
      </div>

      {/* Include Momma Herd Heifers modal */}
      {showHeiferModal && (
        <IncludeHeifersModal
          cattle={cattle}
          weighIns={weighIns}
          calvingRecs={calvingRecs}
          comments={comments}
          breedOpts={breedOpts}
          originOpts={originOpts}
          fmt={fmt}
          authState={authState}
          initialIncludes={includes}
          canEdit={canEdit}
          onConfirm={saveHeiferIncludesAndClose}
          onClose={() => setShowHeiferModal(false)}
          sb={sb}
          reload={loadAll}
        />
      )}
    </div>
  );
};

// ── small UI subcomponents ────────────────────────────────────────────────────

function SummaryTile({
  label,
  value,
  sub,
  color = 'var(--text-primary)',
  labelStrong = false,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  color?: string;
  labelStrong?: boolean;
}) {
  return (
    <div style={tile}>
      <div
        data-summary-tile-label={label}
        style={{
          fontSize: 11,
          color: 'var(--ink-muted)',
          marginBottom: 4,
          fontWeight: labelStrong ? 800 : 600,
        }}
      >
        {label}
      </div>
      <div style={{fontSize: 22, fontWeight: 700, color, lineHeight: 1}}>{value}</div>
      {sub && <div style={{fontSize: 11, color: 'var(--ink-faint)', marginTop: 3}}>{sub}</div>}
    </div>
  );
}

function NumField({
  label,
  value,
  onChange,
  disabled,
  step = '1',
  min,
  max,
}: {
  label: string;
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}) {
  return (
    <div>
      <label style={lbl}>{label}</label>
      <input
        type="number"
        value={value == null ? '' : value}
        step={step}
        min={min}
        max={max}
        disabled={!!disabled}
        onChange={(e) => onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
        style={{...inpS, background: disabled ? 'var(--surface-2)' : 'var(--surface)'}}
      />
    </div>
  );
}

function ForecastChart({
  buckets,
  actualBatches,
}: {
  buckets: ForecastMonthBucket[];
  actualBatches: CattleProcessingBatchRow[];
}) {
  // Q48: counts per period stay bars — projected OUTLINED, actual SOLID, both
  // brand green (charts are green outside the Home island, C17), value labels
  // in gray (D02), legend chips under the chart, and one card for the active
  // month that opens on hover, focus and tap (D04/D09). Inline SVG, no chart
  // library. The max also counts actual batches so both share one scale.
  const [activeMonth, setActiveMonth] = React.useState<string | null>(null);
  const actualByMonth = new Map();
  for (const rb of actualBatches || []) {
    const dt = rb.actual_process_date || rb.planned_process_date;
    if (!dt) continue;
    const k = String(dt).slice(0, 7);
    actualByMonth.set(k, (actualByMonth.get(k) || 0) + (Array.isArray(rb.cows_detail) ? rb.cows_detail.length : 0));
  }
  const allCounts = [...buckets.map((b) => b.count), ...actualByMonth.values()];
  const yMax = Math.max(1, ...allCounts);
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const cellWidth = 70;
  const cellHeight = 140;
  const totalWidth = cellWidth * buckets.length + 30;
  const active = activeMonth ? buckets.find((b) => b.monthKey === activeMonth) || null : null;
  const activeActual = active ? actualByMonth.get(active.monthKey) || 0 : 0;
  return (
    <div style={tile} data-forecast-chart-card="1">
      <div style={{...sectionHeader, color: 'var(--ink)', fontWeight: 700, letterSpacing: 0}}>
        Projected Head Count By Month
      </div>
      {buckets.length === 0 ? (
        // F06: the title stays, with one gray line, when there is nothing to plot.
        <p style={{margin: 0, fontSize: 13, color: 'var(--text-2)'}}>No months to show for this year.</p>
      ) : (
        <div style={{overflowX: 'auto'}} data-forecast-chart>
          <svg
            width={Math.max(totalWidth, 600)}
            height={cellHeight + 60}
            style={{display: 'block'}}
            role="img"
            aria-label="Projected and actual head count by month"
            onMouseLeave={() => setActiveMonth(null)}
          >
            {/* baseline only */}
            <line x1="20" y1={cellHeight + 20} x2={totalWidth + 10} y2={cellHeight + 20} stroke="var(--border)" />
            {buckets.map((b, i) => {
              const x = 30 + i * cellWidth;
              const month = parseMonthKey(b.monthKey)?.month || 1;
              const projHeight = (b.count / yMax) * cellHeight;
              const actualCount = actualByMonth.get(b.monthKey) || 0;
              const actualHeight = (actualCount / yMax) * cellHeight;
              const isActive = activeMonth === b.monthKey;
              const labelTop = cellHeight + 20 - Math.max(projHeight, actualHeight) - 6;
              return (
                <g key={b.monthKey}>
                  {/* Projected: outlined */}
                  {b.count > 0 && (
                    <rect
                      x={x}
                      y={cellHeight + 20 - projHeight}
                      width={cellWidth - 18}
                      height={projHeight}
                      fill="var(--surface)"
                      stroke="var(--brand)"
                      strokeWidth={isActive ? 2.5 : 1.5}
                      data-chart-projected={b.monthKey}
                    />
                  )}
                  {/* Actual: solid, narrower, drawn over the projected outline */}
                  {actualCount > 0 && (
                    <rect
                      x={x + 6}
                      y={cellHeight + 20 - actualHeight}
                      width={cellWidth - 30}
                      height={actualHeight}
                      fill="var(--brand)"
                      data-chart-actual={b.monthKey}
                    />
                  )}
                  {/* Value label (gray) above the taller bar: projected · actual */}
                  {(b.count > 0 || actualCount > 0) && (
                    <text
                      x={x + (cellWidth - 18) / 2}
                      y={labelTop}
                      textAnchor="middle"
                      fontSize="11"
                      fill="var(--text-2)"
                      fontWeight="600"
                    >
                      {actualCount > 0 ? `${b.count} · ${actualCount}` : b.count}
                    </text>
                  )}
                  <text
                    x={x + (cellWidth - 18) / 2}
                    y={cellHeight + 38}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--text-2)"
                  >
                    {labels[month - 1]}
                  </text>
                  {/* One hit target per month: hover, focus and tap open its card. */}
                  <rect
                    x={x - 4}
                    y={10}
                    width={cellWidth - 10}
                    height={cellHeight + 34}
                    fill="transparent"
                    role="button"
                    tabIndex={0}
                    aria-label={`${b.label}: ${b.count} projected, ${actualCount} actual`}
                    aria-pressed={isActive}
                    data-chart-month={b.monthKey}
                    style={{cursor: 'pointer'}}
                    onMouseEnter={() => setActiveMonth(b.monthKey)}
                    onFocus={() => setActiveMonth(b.monthKey)}
                    onClick={() => setActiveMonth(b.monthKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setActiveMonth(null);
                    }}
                  />
                </g>
              );
            })}
          </svg>
        </div>
      )}
      {active && (
        <div
          aria-live="polite"
          data-forecast-chart-detail={active.monthKey}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 14px',
            marginTop: 8,
            padding: '10px 12px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-control)',
            background: 'var(--surface)',
            fontSize: 13,
            color: 'var(--text-2)',
          }}
        >
          <strong style={{color: 'var(--ink)'}}>{active.label}</strong>
          <span>{active.count} projected</span>
          <span>{activeActual} actual</span>
        </div>
      )}
      {buckets.length > 0 && (
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8}}>
          <LegendChip variant="outline" label="Projected (planned)" />
          <LegendChip variant="solid" label="Actual (sent to processor)" />
        </div>
      )}
    </div>
  );
}

// D02/Q48: legend chips under the chart, one per series.
function LegendChip({variant, label}: {variant: 'outline' | 'solid'; label: string}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        borderRadius: 999,
        background: 'var(--surface-2)',
        fontSize: 12,
        color: 'var(--text-2)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: 999,
          boxSizing: 'border-box',
          background: variant === 'solid' ? 'var(--brand)' : 'var(--surface)',
          border: '1.5px solid var(--brand)',
        }}
      />
      {label}
    </span>
  );
}

function MonthBucketTile({
  sb,
  bucket,
  forecast,
  cowsById,
  canEdit,
  onToggleHidden,
  monthFilter,
  setMonthFilter,
  fmt,
  tagMatch,
}: {
  sb: any;
  authState: AuthState;
  bucket: ForecastMonthBucket;
  forecast: ForecastResult;
  cowsById: Map<string, ForecastCattle>;
  canEdit: boolean;
  onToggleHidden: (cattleId: string, monthKey: string, currentlyHidden: boolean) => Promise<void>;
  monthFilter: string | null;
  setMonthFilter: PersistentSetter<string | null>;
  fmt: (iso: string | null | undefined) => string;
  tagMatch: TagMatcher;
}) {
  const {authState} = useAuth();
  const navigate = useAppNavigate();
  const {useCallback, useState} = React;
  // Keep the current default expansion and month-filter behavior.
  const nowYm = new Date().toISOString().slice(0, 7);
  const [expanded, setExpanded] = useState(monthFilter === bucket.monthKey || bucket.monthKey >= nowYm);
  const isFiltered = monthFilter === bucket.monthKey;
  const activityEventFilter = useCallback(
    (event: unknown) => {
      const ev = event as AnyRecord | null | undefined;
      return ev?.payload?.month_key === bucket.monthKey;
    },
    [bucket.monthKey],
  );
  const plannedCount = bucket.count;
  const bookedDate =
    (bucket.scheduledBatches || []).find((b) => b && b.planned_process_date)?.planned_process_date ?? null;
  const hiddenHereOnly = (bucket.hiddenAnimalIds || []).filter((cid) => !bucket.animalIds.includes(cid));
  const visibleHiddenIds = hiddenHereOnly.filter((cid) => {
    const cow = cowsById.get(cid);
    return cow && tagMatch({cow});
  });
  const actualBatches = bucket.actualBatches || [];
  // Active batches still count as processed, matching the existing summary.
  const processedCount = actualBatches.reduce(
    (sum, batch) => sum + (Array.isArray(batch.cows_detail) ? batch.cows_detail.length : 0),
    0,
  );
  const hasContent = bucket.animalIds.length > 0 || hiddenHereOnly.length > 0 || actualBatches.length > 0;
  const isOpen = expanded && hasContent;
  const contentsId = 'cattle-forecast-month-' + bucket.monthKey;

  return (
    <section
      className={'cattle-card cattle-forecast-month' + (isFiltered ? ' cattle-forecast-month-selected' : '')}
      data-month-bucket={bucket.monthKey}
    >
      <button
        type="button"
        className="cattle-section-header cattle-forecast-month-header wcf-clickable"
        disabled={!hasContent}
        aria-expanded={isOpen}
        aria-controls={contentsId}
        onClick={() => {
          setExpanded((value) => !value);
          setMonthFilter(isFiltered ? null : bucket.monthKey);
        }}
      >
        <span className={'cattle-forecast-chevron' + (isOpen ? ' is-open' : '')} aria-hidden="true">
          ›
        </span>
        <span className="cattle-forecast-month-label">{bucket.label}</span>
        {/* Forecast cows are neutral (Q14-A); the month says whether it is booked. */}
        {(plannedCount > 0 || processedCount === 0) && (
          <span
            data-month-planned-count
            className={'cattle-status' + (plannedCount > 0 ? '' : ' cattle-forecast-empty-pill')}
          >
            {plannedCount} {plannedCount === 1 ? 'cow' : 'cows'} forecast
          </span>
        )}
        {bookedDate ? (
          <span data-month-booked style={{fontSize: 12}}>
            <span style={{fontWeight: 700, color: 'var(--text-primary)'}}>{fmt(bookedDate)}</span>{' '}
            <span style={{color: 'var(--ink-muted)'}}>Booked</span>
          </span>
        ) : plannedCount > 0 ? (
          <span data-month-not-booked style={{fontSize: 12, color: 'var(--ink-muted)'}}>
            Not booked
          </span>
        ) : null}
        {processedCount > 0 && (
          <span data-month-processed-count className="cattle-status complete">
            {processedCount} {processedCount === 1 ? 'cow' : 'cows'} processed
          </span>
        )}
        {bucket.overCapacity && <span className="cattle-forecast-capacity">Over capacity</span>}
        {bucket.projectedTotalLbs > 0 && (
          <span className="cattle-forecast-month-total">
            {Math.round(bucket.projectedTotalLbs).toLocaleString()} lb projected
          </span>
        )}
      </button>
      {isOpen && (
        <div id={contentsId}>
          {actualBatches.length > 0 && (
            <div className="cattle-forecast-actuals">
              <div className="cattle-forecast-eyebrow">Actual batches</div>
              {actualBatches.map((batch) => {
                const cows = Array.isArray(batch.cows_detail) ? batch.cows_detail : [];
                return (
                  <div key={batch.id} data-actual-batch={batch.id} className="cattle-forecast-actual-batch">
                    <div className="cattle-forecast-actual-header">
                      <strong>{batch.name}</strong>
                      <span className={'cattle-status ' + (batch.status === 'complete' ? 'complete' : 'processor')}>
                        {processingStatusLabel(batch.status)}
                      </span>
                      <span>
                        {cows.length} {cows.length === 1 ? 'cow' : 'cows'}
                      </span>
                      {(batch.actual_process_date || batch.planned_process_date) && (
                        <span>{fmt(batch.actual_process_date || batch.planned_process_date)}</span>
                      )}
                    </div>
                    {cows.length > 0 && (
                      <div className="cattle-table-scroll">
                        <table className="cattle-forecast-actual-table" data-actual-batch-table={batch.id}>
                          <thead>
                            <tr>
                              <th>Tag</th>
                              <th>Sex</th>
                              <th>Herd</th>
                              <th>Age</th>
                              <th>Live</th>
                              <th>Hanging</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cows.map((cowDetail) => {
                              const cow = cowsById.get(cowDetail.cattle_id);
                              if (!tagMatch({tag: cowDetail.tag, cowRow: cow})) return null;
                              const live = parseFloat(cowDetail.live_weight);
                              const hanging = parseFloat(cowDetail.hanging_weight);
                              const processDate = batch.actual_process_date || batch.planned_process_date;
                              const processMs = processDate
                                ? new Date(String(processDate) + 'T12:00:00Z').getTime()
                                : NaN;
                              return (
                                <tr key={cowDetail.cattle_id} data-actual-batch-row={cowDetail.cattle_id}>
                                  <td>
                                    {cow?.id ? (
                                      <AppLink
                                        to={'/cattle/herds/' + encodeURIComponent(cow.id)}
                                        className="cattle-forecast-animal-link"
                                      >
                                        #{cowDetail.tag || cow.tag || '?'}
                                      </AppLink>
                                    ) : (
                                      '#' + (cowDetail.tag || '?')
                                    )}
                                  </td>
                                  <td>{cow?.sex || '—'}</td>
                                  <td>{cow ? HERD_LABELS[cow.herd || ''] || cow.herd : '—'}</td>
                                  <td data-actual-batch-row-age={cowDetail.cattle_id}>
                                    {ageAt(cow?.birth_date, processMs)}
                                  </td>
                                  <td>
                                    {Number.isFinite(live) && live > 0
                                      ? Math.round(live).toLocaleString() + ' lb'
                                      : '—'}
                                  </td>
                                  <td>
                                    {Number.isFinite(hanging) && hanging > 0
                                      ? Math.round(hanging).toLocaleString() + ' lb'
                                      : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {(bucket.animalIds.length > 0 || hiddenHereOnly.length > 0) && (
            <div className="cattle-table-scroll">
              <div
                className="cattle-forecast-table"
                data-month-bucket-table
                role="table"
                aria-label={bucket.label + ' cattle forecast'}
              >
                <div className="cattle-forecast-grid cattle-forecast-table-head" role="row">
                  <span role="columnheader">Tag</span>
                  <span role="columnheader">Sex</span>
                  <span role="columnheader">Herd</span>
                  <span role="columnheader">Origin</span>
                  <span role="columnheader" className="cattle-forecast-number">
                    Age
                  </span>
                  <span role="columnheader" className="cattle-forecast-number">
                    Latest
                  </span>
                  <span role="columnheader" className="cattle-forecast-number">
                    Projected
                  </span>
                  <span role="columnheader">Avg daily gain</span>
                  <span role="columnheader" className="cattle-forecast-number">
                    Action
                  </span>
                </div>
                {bucket.animalIds.map((cid) => {
                  const cow = cowsById.get(cid);
                  if (!cow || !tagMatch({cow})) return null;
                  const row = forecast.animalRows.find((animal) => animal.cow.id === cid);
                  const [adgValue, ...adgContext] = formatAdgCalc(row).split(' · ');
                  return (
                    <div
                      key={cid}
                      data-month-row={cid}
                      className="cattle-forecast-grid cattle-forecast-row wcf-clickable"
                      {...openableProps(() => navigate('/cattle/herds/' + encodeURIComponent(cid)))}
                      role="row"
                    >
                      <span role="cell">
                        <AppLink
                          to={'/cattle/herds/' + encodeURIComponent(cid)}
                          className="cattle-forecast-animal-link"
                          aria-label={'Open cow #' + (cow.tag || '?')}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        >
                          #{cow.tag || '?'}
                        </AppLink>
                      </span>
                      <span role="cell">{cow.sex || '—'}</span>
                      <span role="cell">{HERD_LABELS[cow.herd || ''] || cow.herd}</span>
                      <span
                        role="cell"
                        data-month-row-origin={cid}
                        className="cattle-forecast-origin"
                        title={cow.origin || undefined}
                      >
                        {cow.origin || '—'}
                      </span>
                      <span role="cell" data-month-row-age={cid} className="cattle-forecast-number">
                        {ageAt(cow.birth_date, monthStartMs(bucket.monthKey) as number)}
                      </span>
                      <span role="cell" className="cattle-forecast-two-line cattle-forecast-number">
                        <span className="cattle-cell-primary">
                          {row?.latest ? Math.round(row.latest.weight).toLocaleString() + ' lb' : '—'}
                        </span>
                        {row?.latest?.date && (
                          <span className="cattle-cell-secondary">{fmt(String(row.latest.date).slice(0, 10))}</span>
                        )}
                      </span>
                      <strong role="cell" className="cattle-forecast-projected cattle-forecast-number">
                        {row?.projectedWeightAtReady
                          ? Math.round(row.projectedWeightAtReady).toLocaleString() + ' lb'
                          : '—'}
                      </strong>
                      <span role="cell" className="cattle-forecast-two-line">
                        <span className={'cattle-cell-primary' + (row?.negativeAdg ? ' cattle-forecast-negative' : '')}>
                          {adgValue}
                        </span>
                        {adgContext.length > 0 && (
                          <span className="cattle-cell-secondary">{adgContext.join(' · ')}</span>
                        )}
                      </span>
                      <span role="cell" className="cattle-forecast-row-action">
                        {canEdit && (
                          <Button
                            variant="secondary"
                            type="button"
                            className="cattle-button cattle-forecast-small-button"
                            data-toggle-hide={cid}
                            aria-label={'Hide cow #' + (cow.tag || '?') + ' from ' + bucket.label}
                            onClick={(event) => {
                              event.stopPropagation();
                              onToggleHidden(cid, bucket.monthKey, false);
                            }}
                          >
                            Hide
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
                {visibleHiddenIds.length > 0 && (
                  <div className="cattle-forecast-hidden-header" role="row">
                    <span role="cell" aria-colspan={9}>
                      Hidden from this month · {visibleHiddenIds.length}
                    </span>
                  </div>
                )}
                {visibleHiddenIds.map((cid) => {
                  const cow = cowsById.get(cid)!;
                  const row = forecast.animalRows.find((animal) => animal.cow.id === cid);
                  // Keep this month's excluded projection, rather than substituting the rollover month's weight.
                  const hideMonthProjected =
                    row &&
                    Number.isFinite(row.anchorWeight) &&
                    Number.isFinite(row.anchorMs) &&
                    Number.isFinite(row.adg)
                      ? projectedWeightAtMonth({
                          anchorWeight: row.anchorWeight as number,
                          anchorMs: row.anchorMs as number,
                          targetMonthKey: bucket.monthKey,
                          adg: row.adg,
                        })
                      : null;
                  const rolledMonth = row?.readyMonth && row.readyMonth !== bucket.monthKey ? row.readyMonth : null;
                  return (
                    <div
                      key={'hidden-' + cid}
                      data-month-hidden-row={cid}
                      className="cattle-forecast-grid cattle-forecast-row cattle-forecast-hidden-row wcf-clickable"
                      {...openableProps(() => navigate('/cattle/herds/' + encodeURIComponent(cid)))}
                      role="row"
                    >
                      <span role="cell">
                        <AppLink
                          to={'/cattle/herds/' + encodeURIComponent(cid)}
                          className="cattle-forecast-animal-link"
                          aria-label={'Open hidden cow #' + (cow.tag || '?')}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                        >
                          #{cow.tag || '?'}
                        </AppLink>
                      </span>
                      <span role="cell">{cow.sex || '—'}</span>
                      <span role="cell">{HERD_LABELS[cow.herd || ''] || cow.herd}</span>
                      <span
                        role="cell"
                        data-month-hidden-row-origin={cid}
                        className="cattle-forecast-origin"
                        title={cow.origin || undefined}
                      >
                        {cow.origin || '—'}
                      </span>
                      <span role="cell" data-month-hidden-row-age={cid} className="cattle-forecast-number">
                        {ageAt(cow.birth_date, monthStartMs(bucket.monthKey) as number)}
                      </span>
                      <span role="cell" className="cattle-forecast-two-line cattle-forecast-number">
                        <span>{row?.latest ? Math.round(row.latest.weight).toLocaleString() + ' lb' : '—'}</span>
                        {row?.latest?.date && (
                          <span className="cattle-cell-secondary">{fmt(String(row.latest.date).slice(0, 10))}</span>
                        )}
                      </span>
                      <span role="cell" data-hidden-projected={cid} className="cattle-forecast-number">
                        {hideMonthProjected != null && Number.isFinite(hideMonthProjected)
                          ? Math.round(hideMonthProjected).toLocaleString() + ' lb'
                          : '—'}
                      </span>
                      <span role="cell">
                        {rolledMonth ? (
                          <span className="cattle-forecast-rollover">→ {monthLabel(rolledMonth)}</span>
                        ) : row?.readyMonth ? (
                          'Hidden'
                        ) : (
                          'No eligible month'
                        )}
                      </span>
                      <span role="cell" className="cattle-forecast-row-action">
                        {canEdit && (
                          <Button
                            variant="secondary"
                            type="button"
                            className="cattle-button cattle-forecast-small-button cattle-forecast-unhide"
                            data-toggle-unhide={cid}
                            aria-label={'Unhide cow #' + (cow.tag || '?') + ' in ' + bucket.label}
                            onClick={(event) => {
                              event.stopPropagation();
                              onToggleHidden(cid, bucket.monthKey, true);
                            }}
                          >
                            Unhide
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div data-month-activity-log={bucket.monthKey} className="cattle-forecast-activity">
            <RecordCollaborationSection
              sb={sb}
              authState={authState}
              entityType="cattle.forecast"
              entityId="cattle-forecast"
              entityLabel={'Cattle Forecast ' + bucket.label}
              activityLimit={200}
              activityEventFilter={activityEventFilter}
              showComments={false}
            />
          </div>
        </div>
      )}
    </section>
  );
}
function AttentionSection({
  rows,
  canEdit,
  onToggleHidden,
  fmt,
  tagMatch,
}: {
  rows: ForecastAnimalRow[];
  canEdit: boolean;
  onToggleHidden: (cattleId: string, monthKey: string, currentlyHidden: boolean) => Promise<void>;
  fmt: (iso: string | null | undefined) => string;
  tagMatch: TagMatcher;
}) {
  const visible = (rows || []).filter((r) => (tagMatch ? tagMatch({cow: r.cow}) : true));
  if (visible.length === 0) {
    return (
      <div style={tile} data-forecast-attention data-forecast-watchlist>
        <div style={sectionHeader}>Needs Attention</div>
        <div
          data-attention-empty
          style={{fontSize: 12, color: 'var(--ink-muted)', fontStyle: 'italic', padding: '4px 0'}}
        >
          {rows && rows.length > 0 ? 'No matching attention rows.' : 'No cattle need attention.'}
        </div>
      </div>
    );
  }
  return (
    <div style={tile} data-forecast-attention data-forecast-watchlist>
      <div style={sectionHeader}>
        Needs Attention
        <span style={{fontWeight: 400, color: 'var(--ink-faint)', marginLeft: 6}}>({visible.length})</span>
      </div>
      <div style={{fontSize: 11, color: 'var(--ink-muted)', marginBottom: 8}}>
        Cattle that don't land cleanly in a planned month. Each row shows the latest weight, ADG, and why the cow didn't
        get a planned month — adjust the underlying data (weigh-in, DOB) or unhide a month to bring her back.
      </div>
      <div style={{overflowX: 'auto'}}>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 12}}>
          <thead>
            <tr
              style={{
                textAlign: 'left',
                color: 'var(--ink-muted)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <th style={{padding: '4px 8px'}}>Tag</th>
              <th style={{padding: '4px 8px'}}>Herd</th>
              <th style={{padding: '4px 8px', textAlign: 'right'}}>Latest</th>
              <th style={{padding: '4px 8px'}}>ADG Calc</th>
              <th style={{padding: '4px 8px'}}>Reason</th>
              <th style={{padding: '4px 8px'}}>Hidden months</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.cow.id} data-watchlist-row={r.cow.id} style={{borderTop: '1px solid var(--divider)'}}>
                <td style={{padding: '6px 8px', fontWeight: 700, color: 'var(--ink)'}}>
                  {r.cow.id ? (
                    <AppLink to={'/cattle/herds/' + encodeURIComponent(r.cow.id)} style={{color: 'inherit'}}>
                      #{r.cow.tag || '?'}
                    </AppLink>
                  ) : (
                    '#' + (r.cow.tag || '?')
                  )}
                </td>
                <td style={{padding: '6px 8px', color: 'var(--ink-muted)'}}>
                  {HERD_LABELS[r.cow.herd || ''] || r.cow.herd}
                </td>
                <td style={{padding: '6px 8px', textAlign: 'right', color: 'var(--ink)'}}>
                  {r.latest
                    ? Math.round(r.latest.weight).toLocaleString() +
                      ' lb' +
                      (r.latest.date
                        ? ' · ' + (fmt ? fmt(String(r.latest.date).slice(0, 10)) : String(r.latest.date).slice(0, 10))
                        : '')
                    : r.cow.birth_date
                      ? 'no weigh-in · DOB ' + r.cow.birth_date
                      : 'no weigh-in · no DOB'}
                </td>
                <td style={{padding: '6px 8px', color: 'var(--ink-muted)', fontSize: 11}}>{formatAdgCalc(r)}</td>
                <td style={{padding: '6px 8px', color: 'var(--warn-ink)', fontSize: 11}}>
                  {r.watchlistReasons.length > 0
                    ? r.watchlistReasons.map((x) => WATCHLIST_REASON_LABELS[x] || x).join(', ')
                    : r.readyMonth
                      ? 'projected ready ' + monthLabel(r.readyMonth)
                      : 'no eligible month'}
                </td>
                <td style={{padding: '6px 8px', fontSize: 11}}>
                  {r.hiddenInMonths && r.hiddenInMonths.length > 0 ? (
                    <span style={{display: 'flex', gap: 4, flexWrap: 'wrap'}}>
                      {r.hiddenInMonths.map((mk) => (
                        <span
                          key={mk}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '1px 6px',
                            borderRadius: 999,
                            background: 'var(--surface-2)',
                            color: 'var(--text-primary)',
                            fontSize: 10,
                            fontWeight: 600,
                          }}
                        >
                          {monthLabel(mk)}
                          {canEdit && (
                            <button
                              onClick={() => onToggleHidden(r.cow.id as string, mk, true)}
                              data-watchlist-unhide={r.cow.id + '|' + mk}
                              title={'Unhide ' + monthLabel(mk)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                fontSize: 11,
                                padding: 0,
                                marginLeft: 2,
                                fontWeight: 700,
                              }}
                            >
                              ×
                            </button>
                          )}
                        </span>
                      ))}
                    </span>
                  ) : (
                    <span style={{color: 'var(--ink-faint)'}}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PastActuals({
  batches,
  fmt,
}: {
  batches: CattleProcessingBatchRow[];
  fmt: (iso: string | null | undefined) => string;
}) {
  if (!batches || batches.length === 0) return null;
  return (
    <div style={tile} data-forecast-past-actuals>
      <div style={sectionHeader}>Past Actual Batches</div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
        {batches.map((rb) => {
          const cows = Array.isArray(rb.cows_detail) ? rb.cows_detail : [];
          return (
            <div
              key={rb.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 8px',
                background: rb.status === 'complete' ? 'var(--surface-2)' : 'var(--danger-soft)',
                borderRadius: 10,
                fontSize: 12,
              }}
            >
              <strong>{rb.name}</strong>
              <Badge variant={rb.status === 'complete' ? 'neutral' : 'info'}>{processingStatusLabel(rb.status)}</Badge>
              <span style={{color: 'var(--ink-muted)'}}>
                {cows.length} {cows.length === 1 ? 'cow' : 'cows'}
              </span>
              <span style={{color: 'var(--ink-muted)'}}>{fmt(rb.actual_process_date || rb.planned_process_date)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── IncludeHeifersModal ───────────────────────────────────────────────────────
function IncludeHeifersModal({
  cattle,
  weighIns,
  calvingRecs,
  comments,
  breedOpts,
  originOpts,
  fmt,

  initialIncludes,
  canEdit,
  onConfirm,
  onClose,
  sb,
  reload,
}: {
  cattle: ForecastCattle[];
  weighIns: ForecastWeighInRow[];
  calvingRecs: AnyRecord[];
  comments: AnyRecord[];
  breedOpts: AnyRecord[];
  originOpts: AnyRecord[];
  fmt: (iso: string | null | undefined) => string;
  authState?: AuthState;
  initialIncludes: ReadonlySet<string>;
  canEdit: boolean;
  onConfirm: (nextSet: ReadonlySet<string>) => Promise<void>;
  onClose: () => void;
  sb: any;
  reload: () => Promise<void>;
}) {
  const {useState, useMemo} = React;
  // Filter + sort momma-herd heifers for the include modal.
  //  - Filter: isHeiferEligibleForInclude removes pregnant heifers and
  //    heifers older than 15 months. No-DOB heifers stay visible per Ronnie's
  //    instruction.
  //  - Sort: youngest first by birth_date desc; no-DOB rows sink to the
  //    bottom. Tie-break on tag (numeric when both are ints, else string).
  const todayMs = useMemo(() => Date.now(), []);
  const heifers = useMemo(() => {
    const list = cattle.filter((c) => isHeiferEligibleForInclude(c, todayMs));
    const tagKey = (c: ForecastCattle) => {
      const t = String(c.tag || '');
      const n = parseInt(t, 10);
      return Number.isFinite(n) && String(n) === t ? n : Number.POSITIVE_INFINITY;
    };
    return list.slice().sort((a, b) => {
      const aHas = !!a.birth_date;
      const bHas = !!b.birth_date;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      if (aHas && bHas) {
        const cmp = String(b.birth_date).localeCompare(String(a.birth_date));
        if (cmp !== 0) return cmp;
      }
      const an = tagKey(a);
      const bn = tagKey(b);
      if (an !== bn) return an - bn;
      return String(a.tag || '').localeCompare(String(b.tag || ''));
    });
  }, [cattle, todayMs]);
  // Stale-include guard at the modal level. cattle_forecast_heifer_includes
  // can carry IDs that point at heifers who are now ineligible (pregnant,
  // over 15 months, or auto-promoted to cow on calving via mig 044). Those
  // rows must NOT count as "selected" in the modal — and on Confirm
  // Selections the diff-based save in cattleForecastApi.saveHeiferIncludes
  // will then DELETE them from the DB because they're no longer in the
  // staged set. This is the modal-side prune; buildForecast also has a
  // helper-level guard so stale rows can never leak into the forecast even
  // if Ronnie never opens the modal.
  const eligibleHeiferIds = useMemo(
    () => new Set(heifers.map((h) => h.id).filter((id): id is string => Boolean(id))),
    [heifers],
  );
  const initialEligibleIncludes = useMemo(() => {
    const out = new Set<string>();
    for (const id of initialIncludes || []) if (eligibleHeiferIds.has(id)) out.add(id);
    return out;
  }, [initialIncludes, eligibleHeiferIds]);
  const [staged, setStaged] = useState<Set<string>>(() => new Set(initialEligibleIncludes));
  const pendingAddCount = useMemo(
    () => [...staged].filter((id) => !initialEligibleIncludes.has(id)).length,
    [staged, initialEligibleIncludes],
  );
  const pendingRemoveCount = useMemo(
    () => [...initialEligibleIncludes].filter((id) => !staged.has(id)).length,
    [staged, initialEligibleIncludes],
  );
  // Mid-flight prune: if `cattle` reloads while the modal is open and a
  // staged heifer becomes ineligible (newly pregnant, aged out, or
  // auto-promoted to cow on calving), drop her from the staged set so
  // Confirm can't re-write her ID into the DB. patchCow inside the row's
  // CowDetail triggers a parent reload which re-flows the cattle prop down
  // and re-runs this effect.
  React.useEffect(() => {
    setStaged((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (eligibleHeiferIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [eligibleHeiferIds]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [modalNotice, setModalNotice] = useState<Notice | null>(null);
  // Handed THIS modal's `sb` prop, which shadows the module singleton
  // deliberately — the retired `runMutation` thunk closed over the same prop,
  // and a conversion that silently swapped the client would change which
  // session issues the write.
  const patchCattleAnimal = useRouteMutation<CattleAnimalPatchVars, null>(MUTATION.CATTLE_ANIMAL_PATCH, {client: sb});

  function toggle(id: string) {
    if (!canEdit) return;
    setStaged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function patchCow(cowId: string, fields: AnyRecord) {
    if (!cowId || !fields) return;
    setModalNotice(null);
    const cow = cattle.find((c) => c.id === cowId);
    // Migration 245: a tag edit is the audited animal.retag operation (the
    // server composes old_tags, source 'manual'); every other field is the
    // audited animal.patch with per-field compare-and-set — its field.updated
    // Activity row is written in the same transaction, so this modal's
    // best-effort emit is gone.
    const saveKey = `cattle-forecast-cow:${cowId}:${Object.keys(fields).sort().join(',')}`;
    if ('tag' in fields) {
      const saveAttempt = reportSaving(saveKey);
      try {
        await runCattleOperation(sb, {
          requestId: newCattleRequestId(),
          operation: 'animal.retag',
          target: {cattle_id: cowId},
          expected: {tag: (cow?.tag ?? null) as string | null},
          change: {tag: (fields.tag ?? null) as string | null, source: 'manual'},
        });
      } catch (err) {
        const msg = 'Save failed: ' + writeErrorMessage(err);
        setModalNotice({kind: 'error', message: msg});
        reportSaveFailed(saveKey, () => void patchCow(cowId, fields), saveAttempt);
        return;
      }
      reportSaved(saveKey, saveAttempt);
      return;
    }
    const expected: AnyRecord = {};
    for (const key of Object.keys(fields)) expected[key] = cow?.[key] ?? null;
    const saveAttempt = reportSaving(saveKey);
    try {
      await patchCattleAnimal.run({cattleId: cowId, patch: fields, expected});
    } catch (err) {
      const msg = 'Save failed: ' + writeErrorMessage(err);
      setModalNotice({kind: 'error', message: msg});
      reportSaveFailed(saveKey, () => void patchCow(cowId, fields), saveAttempt);
      return;
    }
    reportSaved(saveKey, saveAttempt);
    // `reload` is KEPT rather than dropped, and the asymmetry with the herds
    // index is deliberate. There the retired refetch was provably the view's
    // own catalog resource, which `cattle.animal.patch` invalidates. Here
    // `reload` is a PROP: this modal cannot see what its parent refreshes, so
    // dropping it would trade a checked invalidation for an unchecked guess
    // about somebody else's surface. Over-refreshing is not a defect.
    if (reload) await reload();
  }

  return (
    <div
      onClick={onClose}
      style={modalBackdropStyle({
        alignItems: 'flex-start',
        overflowY: 'auto',
      })}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={modalDialogStyle({
          maxWidth: 920,
          marginTop: 40,
        })}
        data-include-heifers-modal
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{fontSize: 15, fontWeight: 600, color: 'var(--text-primary)'}}>Include Momma Herd Heifers</div>
          <button
            onClick={onClose}
            style={{background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--ink-faint)'}}
          >
            {'×'}
          </button>
        </div>
        <div style={{padding: '8px 20px 0'}}>
          <InlineNotice notice={modalNotice} onDismiss={() => setModalNotice(null)} />
        </div>
        <div style={{padding: '8px 20px 4px', fontSize: 11, color: 'var(--ink-muted)'}} data-heifer-include-help>
          {heifers.length} heifer{heifers.length === 1 ? '' : 's'} currently in mommas. Checked heifers are included in
          the forecast using global ADG. Uncheck an included heifer to remove her when you{' '}
          <strong>Confirm Selections</strong>. Eligible heifers stay listed so they can be included again later.
        </div>
        <div style={{padding: '12px 20px 0', maxHeight: '60vh', overflowY: 'auto'}}>
          {heifers.length === 0 ? (
            <div style={{fontSize: 12, color: 'var(--ink-faint)', fontStyle: 'italic', padding: '1rem 0'}}>
              No heifers currently classified in the mommas herd.
            </div>
          ) : (
            heifers.map((h) => {
              const heiferId = h.id as string;
              const checked = staged.has(heiferId);
              const wasIncluded = initialEligibleIncludes.has(heiferId);
              const pendingAdd = checked && !wasIncluded;
              const pendingRemove = wasIncluded && !checked;
              const inclusionState = pendingRemove
                ? 'will-remove'
                : pendingAdd
                  ? 'will-include'
                  : checked
                    ? 'included'
                    : 'excluded';
              const badge: HeiferBadge = pendingRemove
                ? {label: 'Will remove', variant: 'neutral'}
                : pendingAdd
                  ? {label: 'Will include', variant: 'info'}
                  : checked
                    ? {label: 'Included', variant: 'ok'}
                    : {label: 'Excluded', variant: 'neutral'};
              const isExpanded = expandedId === h.id;
              const cTags = cowTagSet(h);
              const cowWeighIns = weighIns.filter((w) => cTags.has(String(w.tag)));
              // Latest weigh-in (retag-aware via cowTagSet); show weight + date
              // directly in the row so admin doesn't have to expand Details.
              const latestWi = cowWeighIns
                .slice()
                .sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || ''))[0];
              const latestWeight = latestWi ? parseFloat(String(latestWi.weight)) : null;
              const latestDate = latestWi ? String(latestWi.entered_at || latestWi.date || '').slice(0, 10) : null;
              // Age from DOB (years + months). Quiet dash if missing.
              const ageStr = formatRuminantAge(h.birth_date) || '—';
              const cowCalving = calvingRecs.filter((r) => r.dam_tag === h.tag);
              const cowComments = comments
                .filter((cm) => cm.cattle_id === h.id || cm.cattle_tag === h.tag)
                .slice(0, 20);
              return (
                <div
                  key={h.id}
                  data-heifer-row={h.id}
                  data-heifer-inclusion-state={inclusionState}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    marginBottom: 8,
                    overflow: 'hidden',
                    background: 'var(--surface)',
                    opacity: pendingRemove ? 0.72 : 1,
                  }}
                >
                  <div
                    {...openableProps(() => setExpandedId(isExpanded ? null : (h.id as string)))}
                    className="hoverable-tile"
                    style={{
                      padding: '8px 12px',
                      display: 'grid',
                      gridTemplateColumns: '24px 70px 60px 150px 90px 110px 112px',
                      alignItems: 'center',
                      gap: 10,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!canEdit}
                      onChange={() => toggle(h.id as string)}
                      onClick={(e) => e.stopPropagation()}
                      data-heifer-checkbox={h.id}
                    />
                    <span style={{fontWeight: 700, fontSize: 13, color: 'var(--ink)'}}>#{h.tag || '?'}</span>
                    <span
                      data-heifer-age={h.id}
                      style={{fontSize: 11, color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums'}}
                    >
                      {ageStr}
                    </span>
                    <span
                      data-heifer-latest-weight={h.id}
                      style={{
                        fontSize: 11,
                        color: latestWeight ? 'var(--text-primary)' : 'var(--ink-faint)',
                        fontWeight: latestWeight ? 600 : 400,
                      }}
                    >
                      {latestWeight ? Math.round(latestWeight).toLocaleString() + ' lb' : 'no weigh-in'}
                      {latestDate && fmt ? ' · ' + fmt(latestDate) : ''}
                    </span>
                    <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>{h.breed || '—'}</span>
                    <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>{h.origin || '—'}</span>
                    <span data-heifer-inclusion-badge={h.id} style={{justifySelf: 'end', whiteSpace: 'nowrap'}}>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </span>
                  </div>
                  {isExpanded && (
                    <CowDetail
                      cow={h}
                      ageLabel={ageStr}
                      weighIns={cowWeighIns}
                      calving={cowCalving}
                      comments={cowComments}
                      calves={cattle.filter((x) => x.dam_tag === h.tag)}
                      dam={cattle.find((x) => x.tag === h.dam_tag)}
                      cattleList={cattle}
                      fmt={fmt}
                      HERDS={[...CATTLE_HERD_KEYS]}
                      HERD_LABELS={HERD_LABELS}
                      onEdit={() => {}}
                      onTransfer={() => {}}
                      // No onDelete / onAddCalving / onDeleteCalving here: this
                      // embed has no delete or calving handlers, and CowDetail
                      // hides those buttons when the handler is absent.
                      onComment={() => {}}
                      onEditComment={() => {}}
                      onDeleteComment={() => {}}
                      hideComments={true}
                      onNavigateToCow={() => {}}
                      onNavigateBack={() => setExpandedId(null)}
                      canNavigateBack={false}
                      backToTag={undefined}
                      onPatch={(fields) => patchCow(h.id as string, fields)}
                      onClose={() => setExpandedId(null)}
                      originOpts={originOpts}
                      breedOpts={breedOpts}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span style={{fontSize: 11, color: 'var(--ink-muted)'}} data-heifer-include-footer>
            {staged.size} selected after confirm
            {pendingAddCount > 0 && (
              <span style={{marginLeft: 6, color: 'var(--info)', fontStyle: 'italic'}}>
                +{pendingAddCount} add pending
              </span>
            )}
            {pendingRemoveCount > 0 && (
              <span style={{marginLeft: 6, color: 'var(--warn-ink)', fontStyle: 'italic'}}>
                -{pendingRemoveCount} remove pending
              </span>
            )}
          </span>
          <span style={{flex: 1}} />
          {canEdit && (
            <Button
              variant="primary"
              onClick={async () => {
                setConfirming(true);
                // Defensive sanitize at save time: even if useEffect hasn't
                // fired yet (race during a reload), only persist staged IDs
                // that are CURRENTLY eligible. Pairs with the buildForecast
                // helper-level guard so a stale row can't sneak into either
                // the DB or the forecast totals.
                const sanitized = new Set<string>();
                for (const id of staged) if (eligibleHeiferIds.has(id)) sanitized.add(id);
                await onConfirm(sanitized);
                setConfirming(false);
              }}
              disabled={confirming}
              data-confirm-heifers-btn
            >
              {confirming ? 'Saving…' : 'Confirm Selections'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CattleForecastView;
